import type { labelColors } from '@dovefiniscono/shared';
import type { LocalRecord } from '@dovefiniscono/sync';
import type { Task, TaskList } from './logic';

export type TaskBoard = LocalRecord<'taskBoards'>;
export type TaskLabel = LocalRecord<'taskLabels'>;
export type TaskComment = LocalRecord<'taskComments'>;
export type LabelColor = (typeof labelColors)[number];
export type ChecklistItem = { id: string; text: string; done: boolean };

/** The colours, in the order the pickers show them. */
export const LABEL_COLORS: { value: LabelColor; label: string }[] = [
  { value: 'green', label: 'Verde' },
  { value: 'yellow', label: 'Giallo' },
  { value: 'orange', label: 'Arancione' },
  { value: 'red', label: 'Rosso' },
  { value: 'purple', label: 'Viola' },
  { value: 'blue', label: 'Blu' },
  { value: 'sky', label: 'Azzurro' },
  { value: 'pink', label: 'Rosa' },
  { value: 'gray', label: 'Grigio' },
];

export const colorName = new Map<string, string>(LABEL_COLORS.map((c) => [c.value, c.label]));

const byPosition = <T extends { position: number }>(a: T, b: T) => a.position - b.position;

/** Archived cards are kept but shown nowhere except the archive. */
export const isLive = (task: Task) => !task.archivedAt;

export function activeBoards(boards: readonly TaskBoard[]) {
  return boards.filter((board) => board.archivedAt === null).sort(byPosition);
}

/**
 * The board of a list: its own while it exists (archived too), else the first open board, for
 * lists made before boards. `boards` holds all of them, archived ones included.
 */
export function boardOf(list: TaskList, boards: readonly TaskBoard[]): string | null {
  if (list.boardId && boards.some((board) => board.id === list.boardId)) return list.boardId;
  return activeBoards(boards)[0]?.id ?? null;
}

/** The open lists of a board, in order. Without boards, every list. */
export function listsOfBoard(
  lists: readonly TaskList[],
  /** All boards, archived ones included: the lists of an archived board stay with it. */
  boards: readonly TaskBoard[],
  boardId: string | null,
) {
  return lists
    .filter((list) => list.archivedAt === null && boardOf(list, boards) === boardId)
    .sort(byPosition);
}

/** The cards of a list (null = "In arrivo") in their order, archived ones left out. */
export function cardsOf(tasks: readonly Task[], listId: string | null) {
  return tasks.filter((task) => isLive(task) && task.listId === listId).sort(byPosition);
}

/**
 * The position that puts an item at `index` among `sorted` (which must not contain the item being
 * moved): halfway between its neighbours, or one step past the ends.
 */
export function positionAt(sorted: readonly { position: number }[], index: number): number {
  const before = sorted[index - 1];
  const after = sorted[index];
  if (!before && !after) return 1;
  if (!before) return (after?.position ?? 1) - 1;
  if (!after) return before.position + 1;
  return (before.position + after.position) / 2;
}

/**
 * Where a dragged item lands: the index of the first item whose middle is past the pointer, among
 * the item boxes in order (the dragged one left out).
 */
export function dropIndex(
  boxes: readonly { start: number; size: number }[],
  pointer: number,
): number {
  const index = boxes.findIndex((box) => pointer < box.start + box.size / 2);
  return index === -1 ? boxes.length : index;
}

export function checklistProgress(checklist: readonly ChecklistItem[] | null | undefined) {
  const items = checklist ?? [];
  return { done: items.filter((item) => item.done).length, total: items.length };
}

/** Adds the label, or takes it away if the card has it. */
export function toggleLabel(labelIds: readonly string[] | null | undefined, id: string) {
  const current = labelIds ?? [];
  return current.includes(id) ? current.filter((other) => other !== id) : [...current, id];
}

let counter = 0;
/** A short id for a checklist item, unique on this card. */
export const newItemId = () => `${Date.now().toString(36)}${(counter++).toString(36)}`;

/** The fields of a copy: same card, "(copia)", not done, right after the original. */
export function copyOf(task: Task, siblings: readonly Task[]) {
  // The original stays where it is: the copy goes between it and the next card.
  const sorted = [...siblings.filter((other) => other.id !== task.id), task].sort(byPosition);
  const index = sorted.indexOf(task) + 1;
  return {
    listId: task.listId,
    title: `${task.title} (copia)`.slice(0, 300),
    notes: task.notes,
    dueDate: task.dueDate,
    priority: task.priority,
    completedAt: null,
    position: positionAt(sorted, index),
    labelIds: task.labelIds ?? [],
    checklist: (task.checklist ?? []).map((item) => ({ ...item, id: newItemId(), done: false })),
    cover: task.cover ?? null,
    archivedAt: null,
  };
}

export type DueState = 'done' | 'overdue' | 'today' | 'soon' | 'later';

/** How a due date looks: done, late, today, within two days, or later. */
export function dueState(task: Task, today: string, inTwoDays: string): DueState | null {
  if (!task.dueDate) return null;
  if (task.completedAt) return 'done';
  if (task.dueDate < today) return 'overdue';
  if (task.dueDate === today) return 'today';
  return task.dueDate <= inTwoDays ? 'soon' : 'later';
}

export type ActivityLine = { at: string; text: string; comment?: TaskComment };

/** What happened to a card, newest first: its comments and the moments it was created, done. */
export function activityOf(task: Task, comments: readonly TaskComment[]): ActivityLine[] {
  const lines: ActivityLine[] = [
    { at: task.createdAt, text: 'Scheda creata' },
    ...comments
      .filter((comment) => comment.taskId === task.id)
      .map((comment) => ({ at: comment.createdAt, text: comment.body, comment })),
  ];
  if (task.completedAt) lines.push({ at: task.completedAt, text: 'Segnata come completata' });
  if (task.archivedAt) lines.push({ at: task.archivedAt, text: 'Archiviata' });
  return lines.sort((a, b) => b.at.localeCompare(a.at));
}
